import { useState } from "react";
import styled from "@emotion/styled";
import { Skeleton } from "antd5";

import { useTriggerBidDocumentUpload } from "lib/hooks/api/customer_documents/useTriggerBidDocumentUpload";
import {
  DEFAULT_CUSTOMER_DOCUMENTS_PAGINATION,
  useCustomerDocuments,
} from "../../../lib/hooks/api/customer_documents/useCustomerDocuments";
import {
  CustomerDocumentSortBy,
  CustomerDocumentSortOrder,
  CustomerDocumentTypeFilter,
} from "../../../lib/types/graphQLEnums";
import { Flex, Text } from "../../../styles/utility-components";
import Button from "../../ui/button/Button";
import { UploadTenderDocumentModal } from "../bid_documents/UploadTenderDocumentModal";

import folderIllustration from "../../../../assets/images/bids/folder_illustration.svg";
import tenderDocumentUploaderIllustration from "../../../../assets/images/bids/tender_document_illustration.svg";

type Props = {
  bidId: string;
  navigateToDocs: () => void;
  sourceUrl: string | undefined;
};

export default function TenderDocumentUploader({
  bidId,
  navigateToDocs,
  sourceUrl,
}: Props): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const triggerUpload = useTriggerBidDocumentUpload(bidId);
  const { data: documents, isLoading } = useCustomerDocuments(
    {
      resourceId: bidId,
      sortBy: CustomerDocumentSortBy.FileName,
      sortOrder: CustomerDocumentSortOrder.Asc,
      documentType: CustomerDocumentTypeFilter.BidDocument,
    },
    DEFAULT_CUSTOMER_DOCUMENTS_PAGINATION,
  );

  if (isLoading) {
    return (
      <Container height={200}>
        <Skeleton active />
      </Container>
    );
  }

  if (documents && documents.length > 0) {
    return (
      <UploadedContainer>
        <Flex flexDirection="column" gap={24}>
          <Flex gap={8} flexDirection="column">
            <Text bold h3>
              Tender documents
            </Text>
            <Text description color="sysTextSecondary">
              This is the space that stores the source documents associated with this tender, and is
              used to inform what you see in Bid reports and Bid responses
            </Text>
          </Flex>
          <Button buttonType="secondary" onClick={navigateToDocs} width="fit-content">
            Manage and upload documents
          </Button>
        </Flex>
        <Content>
          <img src={folderIllustration} alt="Folder uploader" style={{ width: 200, height: 200 }} />
        </Content>
      </UploadedContainer>
    );
  }

  return (
    <>
      <Container onClick={() => setIsModalOpen(true)} aria-label="Tender document uploader">
        <Header>
          <Text body bold h3>
            Tender documents
          </Text>
          <Button buttonType="secondary" icon="upload01" onClick={() => setIsModalOpen(true)}>
            Upload documents
          </Button>
        </Header>
        <Content>
          <img
            src={tenderDocumentUploaderIllustration}
            alt="Tender document uploader"
            style={{ width: 96, height: 96 }}
          />
          <Flex flexDirection="column" gap={4}>
            <Text description bold>
              Upload tender documents to speed up the qualification process
            </Text>
            <Text description color="sysTextSecondary">
              We&apos;ll extract all the tender documents key information to help speed up your
              workflow.
            </Text>
          </Flex>
        </Content>
      </Container>
      <UploadTenderDocumentModal
        bidId={bidId}
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        sourceUrl={sourceUrl}
        onSubmit={triggerUpload}
      />
    </>
  );
}

const Container = styled(Flex)<{ height?: number }>(({ theme, height = 320 }) => ({
  width: "100%",
  height,
  display: "flex",
  flexDirection: "column",
  padding: 24,
  borderRadius: 8,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  backgroundColor: theme.colors.sysBackgroundAlternative,
  cursor: "pointer",
}));

const UploadedContainer = styled.div(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  width: "100%",
  maxHeight: 320,
  padding: "24px 32px",
  borderRadius: 8,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

const Header = styled(Flex)({
  flexShrink: 0,
  height: 32,
  flexDirection: "row",
  flexWrap: "wrap",
  justifyContent: "space-between",
  alignItems: "center",
});

const Content = styled(Flex)({
  flex: 1,
  justifyContent: "center",
  alignItems: "center",
  gap: 8,
});
